<template>
	<div class="px-3" :class="{ 'my-2': lineItems.length <= 1 }">
		<b-row v-for="(lineItem, lineIndex) in lineItems" :key="lineIndex">
			<b-col cols="12" sm="11" lg="11">
				<fieldset :class="{ 'scheduler-border': lineItems.length > 1, 'bgc-grey-light': lineItems.length > 1 && lineIndex === 0 }">
					<legend class="scheduler-border text-color-rhapsody-in-blue-2" v-if="lineItems.length > 1">
						{{ FormMSG(10, 'Line') + ' ' + parseInt(lineIndex + 1, 10) }}
					</legend>
					<b-row class="mb-2">
						<b-col cols="12" sm="12" md="6">
							<b-form-group :label="FormMSG(1, 'Description')">
								<b-form-input
									:placeholder="FormMSG(2, 'Add description here ...')"
									v-model="lineItem.description"
									@change="checkValidations(lineIndex)"
								/>
								<div v-if="$v.lineItems.$each[lineIndex].description.$error" class="invalid-feedback">
									{{ FormMSG(3, 'A description is required') }}
								</div>
							</b-form-group>
						</b-col>
						<b-col cols="12" sm="12" md="6">
							<b-form-group :label="FormMSG(12, 'Category')">
								<cost-center-select
									:all-categories="allCategoriesProps"
									:edit-data="lineItem"
									@change="(costCenterItem) => handleLineCategoryChange(costCenterItem, lineIndex)"
									@click.stop
								/>
							</b-form-group>
						</b-col>
					</b-row>
					<b-row>
						<b-col cols="6" sm="6" md="3">
							<b-form-group :label="`${FormMSG(4, 'Net amount')}`">
								<b-input-group>
									<b-form-input
										v-model="lineItem.amount"
										type="number"
										step="0.01"
										min="0"
										placeholder="0"
										lazy-formatter
										:formatter="decimalFormatter"
										:disabled="lineItems.length > 1 && lineIndex === 0"
										@blur="checkValidations(lineIndex)"
										@input="computePrice(lineIndex)"
									/>
									<b-input-group-append>
										<b-input-group-text>
											<currency-svg color="#06263E" opacity="0.85" width="15" height="15" />
										</b-input-group-text>
									</b-input-group-append>
									<div v-if="$v.lineItems.$each[lineIndex].amount.$error" class="invalid-feedback">
										{{ FormMSG(15, 'Net amount is required') }}
									</div>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col cols="6" sm="6" md="3">
							<b-form-group :label="`${FormMSG(11, 'Tax code')}`">
								<v-select
									ref="flag-item"
									:value="getItemTaxFlagValue(lineItem)"
									label="text"
									:options="taxOptionsProps"
									:reduce="(option) => option.value"
									:get-option-label="(option) => (option.item ? option.item.factor : '')"
									placeholder="..."
									:selectable="selectableItem"
									@input="handleVatChange($event, lineIndex)"
								>
									<template #option="option">
										<div class="text-bold" v-if="option.disabled">{{ option.text }}</div>
										<div class="text-color ml-3" v-else>{{ option.text }} - {{ option.item.name }} ({{ option.item.factor }}%)</div>
									</template>
								</v-select>
							</b-form-group>
						</b-col>
						<b-col cols="6" sm="6" md="3">
							<b-form-group :label="`${FormMSG(7, 'Tax amount')}`">
								<b-input-group>
									<b-form-input
										v-model="lineItem.amountVat"
										type="number"
										step="0.01"
										min="0"
										placeholder="0"
										lazy-formatter
										:formatter="decimalFormatter"
										:disabled="(lineItems.length > 1 && lineIndex === 0) || lineItem.projectFlagItems.taxeFlags.length > 0"
										@input="computePrice(lineIndex)"
									/>
									<b-input-group-append>
										<b-input-group-text>
											<currency-svg color="#06263E" opacity="0.85" width="15" height="15" />
										</b-input-group-text>
									</b-input-group-append>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col cols="6" sm="6" md="3">
							<b-form-group :label="`${FormMSG(8, 'Total amount')}`">
								<b-input-group>
									<b-form-input
										v-model="lineItem.amountTotal"
										type="number"
										step="0.01"
										min="0"
										placeholder="0"
										lazy-formatter
										:formatter="decimalFormatter"
										@input="reverseComputePrice(lineIndex)"
									/>
									<b-input-group-append>
										<b-input-group-text>
											<currency-svg color="#06263E" opacity="0.85" width="15" height="15" />
										</b-input-group-text>
									</b-input-group-append>
								</b-input-group>
							</b-form-group>
						</b-col>
					</b-row>
					<b-row class="mt-2 px-0" v-if="canEditExpense && flags.length > 0">
						<b-col :sm="sizeFlagInput(flags.length)" class="d-flex mb-2 w-100" v-for="(flag, index) in flags" :key="index">
							<b-form-group :label="`${flag.name.toUpperCase()}`" class="w-100">
								<v-select
									:value="getItemCustomFlagValue(lineItem, flag)"
									label="name"
									placeholder="..."
									:options="flag.projectFlagItem"
									:reduce="(option) => option.id"
									:disabled="!canEditExpense"
									@input="flagChanged($event, flag.flagId, lineIndex)"
								/>
							</b-form-group>
						</b-col>
					</b-row>
					<b-row>
						<b-col class="mb-2" sm="12" md="12">
							<co-two-clap
								ref="coTwoClapTicket"
								:parent-id="+lineItem.id"
								:entity-type="1"
								:for-create="false"
								:external-carbon-data="lineItem.carbon"
								hide-btn-reviewed
								@co-two-clap:change="(carbon) => handleCoTwoClapChange(carbon, lineIndex)"
							/>
						</b-col>
					</b-row>
					<b-row class="my-2 px-0" v-if="maxMobileScreenWidth">
						<b-col>
							<b-button class="w-100" variant="danger" v-if="lineIndex !== 0" @click="removeLine(lineIndex)">
								<component :is="getLucideIcon('MinusCircle')" :size="16" />
								{{ FormMSG(11454, 'Remove line') }}
							</b-button>
						</b-col>
					</b-row>
				</fieldset>
			</b-col>
			<b-col cols="12" sm="1" lg="1" class="d-flex align-items-center justify-content-center" v-if="!maxMobileScreenWidth">
				<b-button
					variant="outline-danger"
					style="border-radius: 100%; padding: 0.5rem"
					v-b-tooltip.hover
					:title="FormMSG(11454, 'Remove line')"
					v-if="lineIndex !== 0"
					@click="removeLine(lineIndex)"
				>
					<component :is="getLucideIcon('Minus')" :size="24"
				/></b-button>
			</b-col>
		</b-row>
		<b-button class="btn-split-line px-3" :class="lineItems.length <= 1 ? 'mt-4' : 'mt-2'" variant="light" :disabled="isNotSplittable" @click="splitLine">
			<span>{{ FormMSG(9, 'Split line') }}</span>
		</b-button>
	</div>
</template>

<script>
// mixin
import globalMixin from '@/mixins/global.mixin';
import languageMessages from '@/mixins/languageMessages';
import { validationMixin } from 'vuelidate';
import { required, decimal, minLength } from 'vuelidate/lib/validators';
import expensesFlagsMixin from '@/mixins/expensesFlags.mixin';
import invoicesToolsMixin from '@/mixins/invoicesTools.mixin';

// components
import CoTwoClap from '@/modules/carbonclap/components/green/CoTwoClap';
import CostCenterSelect from './CostCodeSelect';
import CurrencySvg from '@/components/Svg/Currency';
// utils
import { isNil } from '@/shared/utils';
import { store } from '@/store';

//consts
import { INIT_DATA_CARBON, EXPENSE_TYPE } from '@/shared/constants';
import _ from 'lodash';

// constants
const LINE_ITEM_STRUCT = {
	amount: 0,
	amountVat: 0,
	amountTotal: 0,
	quantity: 0,
	category: 0,
	description: '',
	costCenter: 'UNDEFINED',
	categoryName: 'UNDEFINED',
	department: 0,
	type: EXPENSE_TYPE.INVOICE,
	carbon: INIT_DATA_CARBON,
	projectFlagItems: {
		customFlags: [],
		taxeFlags: []
	}
};

export default {
	name: 'LineItemDataView',
	mixins: [languageMessages, globalMixin, validationMixin, expensesFlagsMixin, invoicesToolsMixin],
	components: { CoTwoClap, CostCenterSelect, CurrencySvg },
	props: {
		department: {
			type: Number,
			required: true
		},
		isNotSplittable: {
			type: Boolean,
			default: true
		},
		lineItemData: {
			type: Object,
			required: false,
			default: null
		},
		taxOptionsProps: {
			type: Array,
			default: () => []
		},
		allCategoriesProps: {
			type: Array,
			default: () => []
		},
		invoice: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			lineItems: [_.cloneDeep(LINE_ITEM_STRUCT)],
			invoiceFormType: 1,
			isSubmitted: false,
			isSplittedAmountExceeds: false,
			customFlagsList: [],
			// taxFlagsList: [], ???
			initialAmountData: {
				amount: 0,
				amountVat: 0,
				amountTotal: 0,
				quantity: 0,
				category: 0,
				costCenter: 'UNDEFINED',
				categoryName: 'UNDEFINED',
				department: 0
			},
			invoiceFormType: 1,
			carbonData: _.cloneDeep(INIT_DATA_CARBON),
			vatType: null
		};
	},
	computed: {
		currencySymbol() {
			return store.state.myProfile.currencySymbol;
		}
	},
	async mounted() {
		await this.setDefaultCustomFlags(this.lineItemData);
		this.customFlagsList.push(this.newValueCustomFlags);

		this.lineItems[0].flagItemId = 0;
		if (
			!_.isUndefined(this.lineItems[0].projectFlagItems) &&
			!_.isNil(this.lineItems[0].projectFlagItems) &&
			this.lineItems[0].projectFlagItems.taxeFlags.length > 0
		) {
			this.vatType = this.lineItems[0].flagItemId = this.lineItems[0].projectFlagItems.taxeFlags[0].id;
			// this.taxFlagsList.push(this.lineItems[0].projectFlagItems.taxeFlags[0]); ???
		}
	},
	methods: {
		getLineItemUpdate() {
			this.preparePayloadToSubmit();
			return this.lineItems;
		},
		getItemCustomFlagValue(item, flag) {
			const FlagsInItem = item.projectFlagItems.customFlags.map((x) => x.id);
			const idInFlag = flag.projectFlagItem.map((x) => x.id);
			for (const id of FlagsInItem) {
				const value = idInFlag.find((v) => parseInt(v, 10) === parseInt(id, 10));
				if (value) return value;
			}
			return null;
		},
		getItemTaxFlagValue(item) {
			return item.projectFlagItems.taxeFlags.length > 0 ? item.projectFlagItems.taxeFlags.map((x) => x.id)[0] : null;
		},
		selectableItem(item) {
			return !item.disabled;
		},
		handleVatChange(payload, lineIndex) {
			let tax = this.taxOptionsProps.filter((tax) => +payload === +tax.value)[0];
			let taxIds = this.taxOptionsProps.map((taxes) => taxes.value);

			if (!_.isNil(payload)) {
				// console.log({ payload, taxOptionsProps: this.taxOptionsProps.filter((tax) => +payload === +tax.item.id && !tax.disabled)[0].item.factor });
				this.lineItems[lineIndex].flagItemId = +payload;
				const existingFlag = this.lineItems[lineIndex].projectFlagItems.taxeFlags.find((flag) => flag.id === tax.value);
				if (!existingFlag) {
					// remove old registered taxes flags and push the new one for save.
					if (this.lineItems[lineIndex].projectFlagItems.customFlags && this.lineItems[lineIndex].projectFlagItems.customFlags.length > 0) {
						const cleanCustomFlags = this.lineItems[lineIndex].projectFlagItems.customFlags.filter((flag) => !taxIds.includes(flag.id));
						cleanCustomFlags.push(tax.item);
						this.lineItems[lineIndex].projectFlagItems.customFlags = cleanCustomFlags;
					} else this.lineItems[lineIndex].projectFlagItems.customFlags.push(tax.item);
					// replace the old selected taxes flags
					this.lineItems[lineIndex].projectFlagItems.taxeFlags[0] = tax.item;
					// this.taxFlagsList.push(tax.item); ???
				}
			} else {
				// this.expenseItem.amountVat = this.originalVatAmount;
				// this.vatAmountDisabled = false;
				this.lineItems[lineIndex].flagItemId = 0;
				// Remove the tax item from the custom flag array
				this.lineItems[lineIndex].projectFlagItems.customFlags = this.lineItems[lineIndex].projectFlagItems.customFlags.filter(
					(flag) => !taxIds.includes(flag.id)
				);
				this.lineItems[lineIndex].projectFlagItems.taxeFlags = this.lineItems[lineIndex].projectFlagItems.taxeFlags.filter(
					(flag) => !taxIds.includes(flag.id)
				);
			}
			this.computePrice(lineIndex);
		},
		handleLineCategoryChange(payload, itemIndex) {
			this.lineItems[itemIndex].category = !isNil(payload.category) ? +payload.category : this.lineItems[itemIndex].category;
			this.lineItems[itemIndex].costCenter = !isNil(payload.category) ? payload.costcodeText : this.lineItems[itemIndex].costCenter;
			this.lineItems[itemIndex].categoryName = !isNil(payload.category) ? payload.categoryText : this.lineItems[itemIndex].categoryName;
		},
		calculateTotalAmount(itemIndex) {
			const amount = isNaN(parseFloat(this.lineItems[itemIndex].amount)) ? 0 : parseFloat(this.lineItems[itemIndex].amount);
			const amountVat = isNaN(parseFloat(this.lineItems[itemIndex].amountVat)) ? 0 : parseFloat(this.lineItems[itemIndex].amountVat);
			this.lineItems[itemIndex].amountTotal = _.round(parseFloat(amount + amountVat), 2);
		},
		checkIfSplittedAmountExceeds(sumSplitted) {
			return (
				parseFloat(sumSplitted) > parseFloat(this.initialAmountData.amountTotal) ||
				parseFloat(this.lineItems[0].amount) < 0 ||
				parseFloat(this.lineItems[0].amountVat) < 0
			);
		},
		autoCompareAccumulateSplittedAmount(itemIndex) {
			if (this.lineItems.length > 1 && itemIndex !== 0) {
				// calculate accumulate splitted amount.
				const sumAmountSplitted = this.lineItems.reduce((accumulator, currentValue, currentIndex) => {
					if (currentIndex !== 0) {
						return accumulator + currentValue.amount;
					} else return accumulator;
				}, 0);
				const sumAmountVatSplitted = this.lineItems.reduce((accumulator, currentValue, currentIndex) => {
					if (currentIndex !== 0) {
						return accumulator + currentValue.amountVat;
					} else return accumulator;
				}, 0);
				const sumAmountTotalSplitted = this.lineItems.reduce((accumulator, currentValue, currentIndex) => {
					if (currentIndex !== 0) {
						return accumulator + currentValue.amountTotal;
					} else return accumulator;
				}, 0);

				// recompute amounts of initial item.
				this.lineItems[0].amount = _.round(parseFloat(this.initialAmountData.amount) - parseFloat(sumAmountSplitted), 2);
				const vatCodeOfFirst = this.getItemTaxFlagValue(this.lineItems[0]);
				if (vatCodeOfFirst) {
					const vatValueOfFirst = this.taxOptionsProps.filter((tax) => !_.isNil(tax.item) && +vatCodeOfFirst === +tax.item.id)[0].item.factor;
					this.lineItems[0].amountVat = _.round(parseFloat(vatValueOfFirst / 100) * parseFloat(this.lineItems[0].amount), 2);
				} else {
					this.lineItems[0].amountVat = parseFloat(this.initialAmountData.amountVat) - parseFloat(sumAmountVatSplitted);
				}
				this.lineItems[0].amountTotal = _.round(parseFloat(this.lineItems[0].amount) + parseFloat(this.lineItems[0].amountVat), 2);

				// check if the accumulate amounts exceed the amount of the first item.
				this.isSplittedAmountExceeds = this.checkIfSplittedAmountExceeds(sumAmountTotalSplitted);
			}
		},
		computePrice(itemIndex) {
			const vatCode = this.getItemTaxFlagValue(this.lineItems[itemIndex]);
			if (vatCode) {
				const vatValue = this.taxOptionsProps.filter((tax) => !_.isNil(tax.item) && +vatCode === +tax.item.id)[0].item.factor;
				this.lineItems[itemIndex].amountVat = _.round(parseFloat(vatValue / 100) * parseFloat(this.lineItems[itemIndex].amount), 2);
			}
			this.calculateTotalAmount(itemIndex);
			this.autoCompareAccumulateSplittedAmount(itemIndex);
		},
		reverseComputePrice(itemIndex) {
			const vatCode = this.getItemTaxFlagValue(this.lineItems[itemIndex]);
			if (vatCode) {
				const vatValue = this.taxOptionsProps.filter((tax) => !_.isNil(tax.item) && +vatCode === +tax.item.id)[0].item.factor;
				this.lineItems[itemIndex].amount = _.round(parseFloat(this.lineItems[itemIndex].amountTotal) / (parseFloat(vatValue / 100) + 1), 2);
				this.lineItems[itemIndex].amountVat = _.round(parseFloat(vatValue / 100) * parseFloat(this.lineItems[itemIndex].amount), 2);
			} else {
				this.lineItems[itemIndex].amount = _.round(parseFloat(this.lineItems[itemIndex].amountTotal), 2);
				this.lineItems[itemIndex].amountVat = 0;
			}
			this.autoCompareAccumulateSplittedAmount(itemIndex);
		},
		handleCoTwoClapChange(data, lineItemIndex) {
			this.lineItems[lineItemIndex].carbon = {
				...data.carbonData,
				carbonId: data.carbonData.id ? parseInt(data.carbonData.id, 10) : this.lineItems[lineItemIndex].carbon.carbonId
			};
			this.checkValidations(lineItemIndex);
		},
		preparePayloadToSubmit() {
			this.lineItems = this.lineItems.map((lineItem) => {
				return {
					...lineItem,
					date: this.invoice.date,
					type: EXPENSE_TYPE.INVOICE,
					amount: parseFloat(lineItem.amount),
					amountVat: parseFloat(lineItem.amountVat),
					amountTotal: parseFloat(lineItem.amountTotal),
					quantity: parseFloat(lineItem.quantity),
					category: parseInt(lineItem.category),
					department: parseInt(this.department),
					carbon: lineItem.carbon,
					projectFlagItems: lineItem.projectFlagItems,
					expenseItemId: lineItem.id ? parseInt(lineItem.id, 10) : null,
					poId: lineItem.poId ? lineItem.poId : null,
					poItemId: lineItem.poItemId ? lineItem.poItemId : null,
					flagItemId: lineItem.flagItemId ? lineItem.flagItemId : 0,
					action: !isNil(lineItem.action) ? lineItem.action : 0
				};
			});
		},
		splitLine() {
			this.lineItems.push(_.cloneDeep(LINE_ITEM_STRUCT));
			if (this.initialAmountData.amount === 0) {
				this.initialAmountData = {
					amount: this.lineItems[0].amount,
					amountVat: this.lineItems[0].amountVat,
					amountTotal: this.lineItems[0].amountTotal
				};
			}
			this.checkValidations();
		},
		removeLine(index) {
			this.lineItems[0].amount += parseFloat(this.lineItems[index].amount);
			this.lineItems[0].amountVat = parseFloat(this.lineItems[index].amountVat);
			this.lineItems[0].amountTotal = parseFloat(this.lineItems[0].amount) + parseFloat(this.lineItems[0].amountVat);
			this.lineItems.splice(index, 1);
			this.customFlagsList.splice(index, 1);
			this.checkValidations();
		},
		checkValidations(lineIndex = null) {
			this.$v.$touch();
			const carbonIndex = lineIndex ? lineIndex : 0;
			const isInvalidCarbonFields = this.$refs['coTwoClapTicket'] ? this.$refs['coTwoClapTicket'][carbonIndex].isInvalid() : false;
			this.$emit('enable:save', !this.$v.$invalid && !this.isSplittedAmountExceeds && !isInvalidCarbonFields);
		},
		flagChanged(payload, flagId, lineIndex) {
			this.customFlagsList[lineIndex] ? (this.newValueCustomFlags = this.customFlagsList[lineIndex]) : (this.newValueCustomFlags = []);
			this.customFlagChanged(payload, flagId);
			this.customFlagsList[lineIndex]
				? (this.customFlagsList[lineIndex] = this.newValueCustomFlags)
				: this.customFlagsList.push(this.newValueCustomFlags);
			this.lineItems[lineIndex].projectFlagItems.customFlags = [
				...this.newValueCustomFlags.map((item) => item.item),
				...this.lineItems[lineIndex].projectFlagItems.taxeFlags
			];
			//this.$emit('flag:changed', this.lineItems);
		},
		decimalFormatter(value) {
			if (isNil(value) || isNaN(value)) return 0;
			return _.round(value, 2);
		},
		formatLineItem(lineItem) {
			return {
				...lineItem,
				amount: this.decimalFormatter(lineItem.amount),
				amountTotal: this.decimalFormatter(lineItem.amountTotal),
				amountVat: this.decimalFormatter(lineItem.amountVat)
			};
		}
	},
	watch: {
		lineItemData: {
			handler(newVal) {
				this.lineItems[0] = { ...this.lineItems[0], ..._.cloneDeep(this.formatLineItem(newVal)) };
				this.checkValidations();
			},
			deep: true,
			immediate: true
		},
		isSplittedAmountExceeds: {
			handler(newVal) {
				this.checkValidations();
				this.$emit('enable:error', newVal, 'default');
			},
			immediate: true
		}
	},
	validations() {
		return {
			lineItems: {
				required,
				$each: {
					description: {
						required
					},
					amount: {
						required,
						decimal
					}
				}
			}
		};
	}
};
</script>

<style lang="scss" scoped>
.btn-split-line {
	color: rgba(6, 38, 62, 0.45);
	border-color: rgba(6, 38, 62, 0.45);
	background-color: #fff;
}

.btn-split-line > span:hover {
	border-color: rgba(6, 38, 62, 0.85);
	color: rgba(6, 38, 62, 0.65);
}

.btn-split-line > span:disabled {
	color: rgba(6, 38, 62, 0.3);
	border-color: #f4f4f5;
	background-color: #f4f4f5;
}
</style>
